import { useEffect, useState } from "react";
import Container from "@/components/Container";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { UserPlus, Copy, Trash2, RefreshCw, KeyRound, ShieldCheck, X, Check } from "lucide-react";

export default function Team() {
  const { user: me } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: "", name: "" });
  const [tempPassword, setTempPassword] = useState(null); // {email, password, kind: 'invite'|'reset'}
  const [showChange, setShowChange] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm: "" });

  const load = () =>
    api.get("/team")
      .then((r) => setMembers(r.data || []))
      .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const invite = async (e) => {
    e.preventDefault();
    if (!inviteForm.email) return toast.error("Email required");
    try {
      const { data } = await api.post("/team/invite", inviteForm);
      setTempPassword({ email: data.user.email, password: data.temporary_password, kind: "invite" });
      setShowInvite(false);
      setInviteForm({ email: "", name: "" });
      load();
    } catch (er) {
      toast.error(formatApiError(er.response?.data?.detail, "Invite failed"));
    }
  };

  const resetPassword = async (u) => {
    if (!confirm(`Generate a new one-time password for ${u.email}? Their current password will stop working.`)) return;
    try {
      const { data } = await api.post(`/team/${u.id}/reset-password`);
      setTempPassword({ email: u.email, password: data.temporary_password, kind: "reset" });
    } catch (er) {
      toast.error(formatApiError(er.response?.data?.detail, "Reset failed"));
    }
  };

  const removeUser = async (u) => {
    if (u.id === me?.id) return toast.error("You cannot remove your own account");
    if (!confirm(`Remove ${u.email}? They lose all access immediately.`)) return;
    try {
      await api.delete(`/team/${u.id}`);
      toast.success("Admin removed");
      load();
    } catch (er) {
      toast.error(formatApiError(er.response?.data?.detail, "Remove failed"));
    }
  };

  const changeOwnPassword = async (e) => {
    e.preventDefault();
    if (pwForm.new_password.length < 8) return toast.error("New password must be at least 8 characters");
    if (pwForm.new_password !== pwForm.confirm) return toast.error("Passwords do not match");
    try {
      await api.post("/team/change-password", {
        current_password: pwForm.current_password,
        new_password: pwForm.new_password,
      });
      toast.success("Password changed");
      setShowChange(false);
      setPwForm({ current_password: "", new_password: "", confirm: "" });
    } catch (er) {
      toast.error(formatApiError(er.response?.data?.detail, "Change failed"));
    }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied");
    } catch {
      toast.error("Copy blocked — please select the password above and copy manually");
    }
  };

  return (
    <Container className="py-8 md:py-12">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline">Access</p>
          <h1 className="mt-2 text-4xl md:text-5xl font-serif text-ivory">Team</h1>
          <p className="text-ivory/50 mt-2 text-sm">
            Invite additional admins, rotate passwords and remove access — all without a redeploy.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowChange(true)} className="btn-outline" data-testid="team-change-password">
            <KeyRound size={14} /> Change my password
          </button>
          <button onClick={() => setShowInvite(true)} className="btn-ruby" data-testid="team-invite-toggle">
            <UserPlus size={14} /> Invite admin
          </button>
        </div>
      </div>

      {/* Invite modal (inline card) */}
      {showInvite && (
        <form onSubmit={invite} className="luxury-card-ruby p-6 mt-6 grid grid-cols-1 md:grid-cols-6 gap-3" data-testid="team-invite-form">
          <input
            className="bg-charcoal-800 border border-charcoal-600 px-3 py-2 text-sm md:col-span-3"
            placeholder="new-admin@rubys.group"
            value={inviteForm.email}
            onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
            type="email"
            required
            data-testid="team-invite-email"
          />
          <input
            className="bg-charcoal-800 border border-charcoal-600 px-3 py-2 text-sm md:col-span-2"
            placeholder="Name (optional)"
            value={inviteForm.name}
            onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
            data-testid="team-invite-name"
          />
          <button type="submit" className="btn-ruby justify-center" data-testid="team-invite-submit">
            <UserPlus size={13} /> Invite
          </button>
          <p className="md:col-span-6 text-[0.7rem] text-ivory/50">
            A one-time password will be generated. Copy it and share it with the invitee — we won&apos;t show it again.
          </p>
        </form>
      )}

      {/* Change own password modal */}
      {showChange && (
        <form onSubmit={changeOwnPassword} className="luxury-card p-6 mt-6 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="team-change-form">
          <input type="password" placeholder="Current password" required
            className="bg-charcoal-800 border border-charcoal-600 px-3 py-2 text-sm"
            value={pwForm.current_password} onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })}
            data-testid="team-current-password" />
          <input type="password" placeholder="New password (8+)" required
            className="bg-charcoal-800 border border-charcoal-600 px-3 py-2 text-sm"
            value={pwForm.new_password} onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })}
            data-testid="team-new-password" />
          <input type="password" placeholder="Confirm new password" required
            className="bg-charcoal-800 border border-charcoal-600 px-3 py-2 text-sm"
            value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })}
            data-testid="team-confirm-password" />
          <div className="md:col-span-3 flex gap-2">
            <button className="btn-ruby" data-testid="team-change-submit"><Check size={13}/> Save</button>
            <button type="button" onClick={() => setShowChange(false)} className="btn-outline"><X size={13}/> Cancel</button>
          </div>
        </form>
      )}

      {/* One-time password reveal — only shown once */}
      {tempPassword && (
        <div className="luxury-card-ruby p-6 mt-6" data-testid="team-temp-password">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="overline">One-time password · {tempPassword.kind === "invite" ? "New invite" : "Password reset"}</p>
              <p className="mt-2 text-ivory/85 text-sm">For <span className="text-gold">{tempPassword.email}</span>. Copy and share it — this is the only time you&apos;ll see it.</p>
              <div className="mt-4 flex items-center gap-3 flex-wrap">
                <code className="font-mono text-2xl text-gold bg-ink-950/60 px-4 py-2 tracking-wider" data-testid="temp-password-value">
                  {tempPassword.password}
                </code>
                <button onClick={() => copy(tempPassword.password)} className="btn-outline" data-testid="temp-password-copy">
                  <Copy size={13} /> Copy
                </button>
              </div>
            </div>
            <button onClick={() => setTempPassword(null)} className="text-ivory/60 hover:text-ivory" aria-label="Dismiss">
              <X size={18} />
            </button>
          </div>
        </div>
      )}

      {/* Members list */}
      <div className="mt-8 luxury-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="team-table">
          <thead className="bg-charcoal-700 text-ivory/60 text-[0.65rem] uppercase tracking-[0.18em]">
            <tr>
              <th className="text-left p-3">Name</th>
              <th className="text-left p-3">Email</th>
              <th className="text-left p-3">Role</th>
              <th className="text-left p-3">Added</th>
              <th className="text-right p-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="p-6 text-center text-ivory/40">Loading…</td></tr>}
            {!loading && members.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-ivory/40">No team members yet.</td></tr>}
            {members.map((u) => {
              const isMe = u.id === me?.id;
              return (
                <tr key={u.id} className="border-t border-charcoal-600/60 hover:bg-charcoal-800/40" data-testid={`team-row-${u.id}`}>
                  <td className="p-3 text-ivory">
                    <span className="inline-flex items-center gap-2">
                      {u.name || "—"}
                      {isMe && <span className="text-[0.6rem] uppercase tracking-[0.15em] text-gold border border-gold/40 px-1.5 py-0.5">You</span>}
                    </span>
                  </td>
                  <td className="p-3 text-ivory/70">{u.email}</td>
                  <td className="p-3">
                    <span className="inline-flex items-center gap-1.5 text-[0.65rem] uppercase tracking-[0.15em] text-gold">
                      <ShieldCheck size={12} /> {u.role}
                    </span>
                  </td>
                  <td className="p-3 text-ivory/60 text-[0.7rem]">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                  </td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <button onClick={() => resetPassword(u)} className="btn-outline !py-1 !px-3 !text-[0.65rem] mr-1" data-testid={`reset-${u.id}`}>
                      <RefreshCw size={11} /> Reset password
                    </button>
                    <button
                      onClick={() => removeUser(u)}
                      disabled={isMe}
                      className="btn-outline !py-1 !px-2 !text-[0.65rem] !border-ruby/40 !text-ruby-light disabled:opacity-30"
                      data-testid={`remove-${u.id}`}
                    >
                      <Trash2 size={11} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="mt-4 text-[0.7rem] text-ivory/40">
        {members.length} admin{members.length === 1 ? "" : "s"} · you cannot remove yourself or the last remaining admin.
      </p>
    </Container>
  );
}
